import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Plus, Edit2, Trash2, DollarSign, Building2, Building, Eye, X, Clock, User, ChevronDown, ChevronRight, ArrowUp, ArrowDown, Loader2, MapPin, AlertTriangle } from 'lucide-react';
import { jobService } from '../../../services/jobService';
import Header from '../../../components/ui/Header';
import Sidebar from '../../../components/ui/Sidebar';
import { useAuth } from '../../../contexts/AuthContext';
import { useCompany } from '../../../contexts/CompanyContext';
import { useUserPreferences } from '../../../contexts/UserPreferencesContext';
import CreateJobModal from '../../../components/jobs/CreateJobModal';
import InstanceContractorPopover from '../../../components/jobs/InstanceContractorPopover';
import InstanceSchedulePopover from '../../../components/jobs/InstanceSchedulePopover';
import JobInvoice from '../../../components/jobs/JobInvoice';
import { cn } from '../../../utils/cn';

// Helper function to compute job status based on job data
const computeJobStatus = (job) => {
  const instances = job?.job_instances || [];

  if (instances.length === 0) {
    // No instances - check job properties
    if (job?.status) return job.status;
    if (job?.isPublished || job?.assignedContractor) return 'active';
    return 'draft';
  }

  // Count statuses across all instances
  const statusCounts = instances.reduce((acc, i) => {
    const status = i?.status || 'created';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const total = instances.length;
  const completed = (statusCounts.completed || 0) + (statusCounts.approved || 0) + (statusCounts.paid || 0);
  const inProgress = statusCounts.in_progress || 0;
  const submitted = statusCounts.submitted || 0;
  const assigned = statusCounts.assigned || 0;
  const unassigned = statusCounts.unassigned || 0;

  // All completed → completed
  if (completed === total) return 'completed';

  // Any in progress or submitted → active
  if (inProgress > 0 || submitted > 0) return 'active';

  // Any assigned → active
  if (assigned > 0) return 'active';

  // Mix of completed and pending → active (partially done)
  if (completed > 0) return 'active';

  // Any unassigned instances = published job waiting for contractors
  // Check if the job itself is published or has active status
  if (unassigned > 0 && (job?.isPublished || job?.status === 'active')) return 'active';

  // All created/unassigned with no publish flag → draft
  return 'draft';
};

// Custom Reassign Icon - person INSIDE circular refresh arrow (orange person + green arrow)
const ReassignIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none">
    {/* Circular refresh arrow wrapping around (green) */}
    <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" className="text-accent" strokeWidth="2" strokeLinecap="round" />
    <path d="M21 12a9 9 0 0 1-9 9" stroke="currentColor" className="text-accent" strokeWidth="2" strokeLinecap="round" />
    <path d="M12 21a9 9 0 0 1-9-9" stroke="currentColor" className="text-accent" strokeWidth="2" strokeLinecap="round" />
    <path d="M3 12a9 9 0 0 1 6-8.5" stroke="currentColor" className="text-accent" strokeWidth="2" strokeLinecap="round" />
    {/* Arrow head at top */}
    <path d="M12 3l3-2M12 3l3 2" stroke="currentColor" className="text-accent" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    {/* Person silhouette centered inside (orange) */}
    <circle cx="12" cy="9" r="2.5" fill="currentColor" className="text-primary" />
    <path d="M7.5 17c0-2.5 2-4.5 4.5-4.5s4.5 2 4.5 4.5" stroke="currentColor" className="text-primary" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Get instance status color for dark theme
const getInstanceStatusColor = (status) => {
  const colors = {
    created: 'bg-gray-500/20 text-text-secondary',
    unassigned: 'bg-gray-500/20 text-text-secondary',
    assigned: 'bg-blue-500/20 text-blue-300',
    in_progress: 'bg-yellow-500/20 text-yellow-300',
    submitted: 'bg-orange-500/20 text-orange-300',
    completed: 'bg-green-500/20 text-green-300',
    approved: 'bg-emerald-500/20 text-emerald-300',
    paid: 'bg-purple-500/20 text-purple-300'
  };
  return colors[status] || colors.created;
};

// Format status text for display
const formatStatusText = (status) => {
  if (!status) return 'Created';
  return status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

// Format time for display - supports user preference
const formatTime = (timeString, timeFormat = '12h') => {
  if (!timeString) return '—';
  try {
    // Handle both "HH:MM" and "HH:MM:SS" formats
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);

    if (timeFormat === '24h') {
      return `${String(hour).padStart(2, '0')}:${minutes}`;
    }

    // 12h format
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  } catch {
    return timeString;
  }
};

// Format date for display - supports user preference
const formatDate = (dateString, dateFormat = 'DD/MM/YYYY') => {
  if (!dateString) return '—';
  try {
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const monthShort = d.toLocaleDateString('en-AU', { month: 'short' });
    const year = d.getFullYear();

    switch (dateFormat) {
      case 'MM/DD/YYYY':
        return `${monthShort} ${day}, ${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'DD/MM/YYYY':
      default:
        return `${day} ${monthShort} ${year}`;
    }
  } catch {
    return dateString;
  }
};

// Format datetime for created column - supports user preference
const formatCreatedDate = (dateString, dateFormat = 'DD/MM/YYYY') => {
  if (!dateString) return '—';
  try {
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, '0');
    const monthShort = d.toLocaleDateString('en-AU', { month: 'short' });

    switch (dateFormat) {
      case 'MM/DD/YYYY':
        return `${monthShort} ${day}`;
      case 'YYYY-MM-DD':
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${day}`;
      case 'DD/MM/YYYY':
      default:
        return `${day} ${monthShort}`;
    }
  } catch {
    return dateString;
  }
};

// Format schedule for one-time jobs: "2:30 PM on Tue - 22nd Dec '25"
const formatOneTimeSchedule = (dateString, timeString, dateFormat = 'DD/MM/YYYY', timeFormat = '12h') => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    const dayName = date.toLocaleDateString('en-AU', { weekday: 'short' });
    const day = date.getDate();
    const month = date.toLocaleDateString('en-AU', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);

    // Add ordinal suffix (1st, 2nd, 3rd, etc.)
    const getOrdinal = (n) => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    const timeDisplay = timeString ? formatTime(timeString, timeFormat) : null;

    // Format date part based on preference
    let dateDisplay;
    switch (dateFormat) {
      case 'MM/DD/YYYY':
        dateDisplay = `${dayName} - ${month} ${getOrdinal(day)} '${year}`;
        break;
      case 'YYYY-MM-DD':
        dateDisplay = `${dayName} - ${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        break;
      case 'DD/MM/YYYY':
      default:
        dateDisplay = `${dayName} - ${getOrdinal(day)} ${month} '${year}`;
    }

    if (timeDisplay && timeDisplay !== '—') {
      return `${timeDisplay} on ${dateDisplay}`;
    }
    return dateDisplay;
  } catch {
    return dateString;
  }
};

// Format instance schedule: "2:30 PM on Tue - 22nd Dec '25"
const formatInstanceSchedule = (dateString, timeString, dateFormat = 'DD/MM/YYYY', timeFormat = '12h') => {
  return formatOneTimeSchedule(dateString, timeString, dateFormat, timeFormat);
};

// Color constants for alternating receiver groups
const RECEIVER_COLORS = {
  odd: { receiver: 'bg-background', job: 'bg-surface', instance: 'bg-surface-light' },
  even: { receiver: 'bg-background-alt', job: 'bg-surface-alt', instance: 'bg-surface-alt-light' }
};

// Group jobs by Client → Job Site hierarchy
const groupJobsByClientAndJobSite = (jobs) => {
  const clientGroups = new Map();

  jobs?.forEach(job => {
    // Get client info - jobs should have clientId and client relation
    const clientId = job?.clientId || job?.client?.id || 'unassigned';
    const clientName = job?.client?.client_name || job?.client?.clientName || 'Unassigned Client';
    const clientTag = job?.client?.tag || null;

    if (!clientGroups.has(clientId)) {
      clientGroups.set(clientId, {
        client: {
          id: clientId,
          name: clientName,
          tag: clientTag,
          isUnassigned: clientId === 'unassigned'
        },
        jobSites: new Map()
      });
    }

    const clientGroup = clientGroups.get(clientId);

    // Get job site info
    const jobSiteId = job?.jobSiteId || job?.job_site?.id || job?.jobSite?.id || 'unassigned';
    const jobSiteName = job?.job_site?.site_name || job?.jobSite?.siteName || job?.jobSite?.site_name || 'No Site';
    const jobSiteAddress = job?.job_site?.address || job?.jobSite?.address || job?.originalAddress || null;

    if (!clientGroup.jobSites.has(jobSiteId)) {
      clientGroup.jobSites.set(jobSiteId, {
        jobSite: {
          id: jobSiteId,
          name: jobSiteName,
          address: jobSiteAddress,
          isUnassigned: jobSiteId === 'unassigned'
        },
        jobs: []
      });
    }

    clientGroup.jobSites.get(jobSiteId).jobs.push(job);
  });

  // Sort client groups: Unassigned last, then alphabetically
  const sortedClientGroups = Array.from(clientGroups.entries()).sort((a, b) => {
    if (a[0] === 'unassigned') return 1;
    if (b[0] === 'unassigned') return -1;
    return a[1].client.name.localeCompare(b[1].client.name);
  });

  // Sort job sites within each client
  sortedClientGroups.forEach(([, clientData]) => {
    const sortedJobSites = Array.from(clientData.jobSites.entries()).sort((a, b) => {
      if (a[0] === 'unassigned') return 1;
      if (b[0] === 'unassigned') return -1;
      return a[1].jobSite.name.localeCompare(b[1].jobSite.name);
    });
    clientData.jobSites = new Map(sortedJobSites);
  });

  return new Map(sortedClientGroups);
};

// Sort jobs based on sort configuration
const sortJobs = (jobs, sortConfig) => {
  if (!jobs || !sortConfig?.column) return jobs;

  const getSortValue = (job, column) => {
    switch (column) {
      case 'client':
        return job?.client?.client_name || job?.client?.clientName || '';
      case 'priority': {
        const priorities = { high: 3, medium: 2, low: 1 };
        return priorities[job?.priority] || 0;
      }
      case 'status':
        return computeJobStatus(job);
      case 'payment':
        return job?.paymentAmount || 0;
      case 'date': {
        if (job?.jobType === 'one_off') {
          return job?.scheduledDate || '';
        }
        // For recurring, use earliest next occurrence
        const nextInstance = job?.job_instances?.find(i =>
          i?.status !== 'completed' && i?.status !== 'approved' && i?.status !== 'paid'
        );
        return nextInstance?.scheduled_date || job?.scheduledDate || '';
      }
      case 'time': {
        if (job?.jobType === 'one_off') {
          return job?.scheduledTime || '';
        }
        return job?.recurringSchedule?.time || '';
      }
      case 'created':
        return job?.createdAt || '';
      default:
        return '';
    }
  };

  return [...jobs].sort((a, b) => {
    const aValue = getSortValue(a, sortConfig.column);
    const bValue = getSortValue(b, sortConfig.column);

    // Primary sort
    let comparison = 0;
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      comparison = aValue - bValue;
    } else {
      comparison = String(aValue).localeCompare(String(bValue));
    }

    if (sortConfig.dateDirection === 'desc') {
      comparison = -comparison;
    }

    // If sorting by date and values are equal, secondary sort by time
    if (comparison === 0 && sortConfig.column === 'date') {
      const aTime = getSortValue(a, 'time');
      const bTime = getSortValue(b, 'time');
      comparison = String(aTime).localeCompare(String(bTime));
      if (sortConfig.timeDirection === 'desc') {
        comparison = -comparison;
      }
    }

    return comparison;
  });
};

// Sort instances within a job based on sort configuration
const sortInstances = (instances, sortConfig) => {
  if (!instances || instances.length === 0) return instances;

  return [...instances].sort((a, b) => {
    const aDate = new Date(a?.scheduled_date || 0);
    const bDate = new Date(b?.scheduled_date || 0);
    let comparison = aDate - bDate;

    // If same date, sort by time
    if (comparison === 0) {
      const aTime = a?.scheduled_time || '00:00';
      const bTime = b?.scheduled_time || '00:00';
      comparison = aTime.localeCompare(bTime);
    }

    // Apply sort direction
    return sortConfig?.dateDirection === 'desc' ? -comparison : comparison;
  });
};

// Check if an instance is past and completed
const isInstancePastCompleted = (instance) => {
  const today = new Date().toISOString().split('T')[0];
  const status = instance?.status;
  const isCompleted = ['completed', 'approved', 'paid'].includes(status);
  const isPast = instance?.scheduled_date < today;
  return isPast && isCompleted;
};

// Check if an instance should be hidden from Job Management (moved to Reports)
// Instances with status 'submitted', 'approved', 'disputed', or 'paid' should only show in Reports
const isInstanceInReportsMode = (instance) => {
  const status = instance?.status;
  return ['submitted', 'approved', 'disputed', 'paid', 'needs_revision'].includes(status);
};

// Filter job instances to exclude those that are in Reports mode
const filterInstancesForManagement = (instances) => {
  return instances?.filter(inst => !isInstanceInReportsMode(inst)) || [];
};

// Days of week configuration
const WEEKDAYS = [
  { key: 'sun', label: 'S', fullName: 'Sunday' },
  { key: 'mon', label: 'M', fullName: 'Monday' },
  { key: 'tue', label: 'T', fullName: 'Tuesday' },
  { key: 'wed', label: 'W', fullName: 'Wednesday' },
  { key: 'thu', label: 'T', fullName: 'Thursday' },
  { key: 'fri', label: 'F', fullName: 'Friday' },
  { key: 'sat', label: 'S', fullName: 'Saturday' }
];

// Recurring Days Display Component
const RecurringDaysDisplay = ({ schedule, timeFormat = '12h' }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipDay, setTooltipDay] = useState(null);

  const activeDays = schedule?.days || [];
  const pattern = schedule?.pattern || 'fixed';
  const fixedTime = schedule?.time;
  const customTimes = schedule?.customTimes || {};

  // Get time for a specific day (handles both fixed and custom patterns)
  const getTimeForDay = (dayKey) => {
    if (pattern === 'custom') {
      return customTimes[dayKey] || null;
    }
    return fixedTime || null;
  };

  const handleDayClick = (day) => {
    if (activeDays.includes(day.key)) {
      setTooltipDay(day);
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
    setTooltipDay(null);
  };

  return (
    <div className="relative" onMouseLeave={handleMouseLeave}>
      <div className="flex items-center gap-0.5">
        {WEEKDAYS.map((day) => {
          const isActive = activeDays.includes(day.key);
          return (
            <button
              key={day.key}
              type="button"
              onClick={() => handleDayClick(day)}
              onMouseEnter={() => {
                if (isActive) {
                  setTooltipDay(day);
                  setShowTooltip(true);
                }
              }}
              className={cn(
                'w-5 h-5 rounded text-[10px] font-medium flex items-center justify-center transition-colors',
                isActive
                  ? 'bg-accent text-white cursor-pointer hover:bg-accent-600'
                  : 'bg-border text-text-tertiary cursor-default'
              )}
              title={isActive ? `${day.fullName} - Click for time` : day.fullName}
            >
              {day.label}
            </button>
          );
        })}
      </div>

      {/* Time Tooltip */}
      {showTooltip && tooltipDay && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-surface rounded-lg whitespace-nowrap">
          <div className="text-xs text-text-secondary mb-1">{tooltipDay.fullName}</div>
          <div className="text-sm text-text-primary font-medium flex items-center gap-1">
            <Clock className="w-3 h-3 text-accent" />
            {(() => {
              const dayTime = getTimeForDay(tooltipDay.key);
              return dayTime ? formatTime(dayTime, timeFormat) : 'No time set';
            })()}
          </div>
          {/* Tooltip Arrow */}
          <div className="absolute top-full left-1/2 -translate-x-1/2">
            <div className="border-4 border-transparent border-t-surface"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function JobManagement() {
  useAuth();
  const { currentCompany } = useCompany();
  const { preferences } = useUserPreferences();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [viewingJob, setViewingJob] = useState(null); // For job detail view modal
  const [viewingInstance, setViewingInstance] = useState(null); // For instance-level invoice view { job, instance }
  const [modalInitialTab, setModalInitialTab] = useState(null); // For opening modal to specific tab
  const [focusContractorSearch, setFocusContractorSearch] = useState(false); // Auto-focus contractor search
  const [highlightedInstanceId, setHighlightedInstanceId] = useState(null); // For highlighting from notifications

  // Selection state
  const [selectedJobs, setSelectedJobs] = useState(new Set());
  const [selectedInstances, setSelectedInstances] = useState(new Set()); // instanceId

  // Client/JobSite expansion state
  const [expandedClients, setExpandedClients] = useState(new Set()); // Expand all by default

  // Instance assignment popover state
  const [assigningInstance, setAssigningInstance] = useState(null); // { instance, job, position }
  const [editingInstanceSchedule, setEditingInstanceSchedule] = useState(null); // { instance, job, position }

  // Dropdown expansion state
  const [expandedJobSites, setExpandedJobSites] = useState(new Set());
  const [expandedJobs, setExpandedJobs] = useState(new Set());
  const [instancePages, setInstancePages] = useState({}); // { jobId: pageNumber }

  // Sorting state - default to schedule date ascending (earliest first)
  const [sortConfig, setSortConfig] = useState({
    column: 'date',
    dateDirection: 'asc',
    timeDirection: 'asc'
  });

  // State for tracking instance creation
  const [creatingInstances, setCreatingInstances] = useState(new Set());

  // Toggle client expansion
  const toggleClient = (clientId) => {
    setExpandedClients(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
      }
      return next;
    });
  };

  // Toggle job site expansion
  const toggleJobSite = (jobSiteKey) => {
    setExpandedJobSites(prev => {
      const next = new Set(prev);
      if (next.has(jobSiteKey)) {
        next.delete(jobSiteKey);
      } else {
        next.add(jobSiteKey);
      }
      return next;
    });
  };

  // Toggle job expansion (for instances)
  const toggleJob = (jobId) => {
    setExpandedJobs(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) {
        next.delete(jobId);
      } else {
        next.add(jobId);
      }
      return next;
    });
  };

  // Load more instances for a job
  const loadMoreInstances = (jobId) => {
    setInstancePages(prev => ({
      ...prev,
      [jobId]: (prev[jobId] || 1) + 1
    }));
  };

  // Create more recurring instances for a job
  const createMoreInstances = async (job) => {
    if (!job?.id || !currentCompany?.id) return;

    // Check if already creating
    if (creatingInstances.has(job.id)) return;

    // Get existing instances to find the last scheduled date
    const existingInstances = job?.job_instances || [];
    const lastInstance = existingInstances.length > 0
      ? existingInstances.reduce((latest, inst) => {
        const instDate = new Date(inst?.scheduled_date);
        return instDate > new Date(latest?.scheduled_date) ? inst : latest;
      }, existingInstances[0])
      : null;

    // Check if we've reached the end date
    if (job?.recurringEndDate) {
      const endDate = new Date(job.recurringEndDate);
      const lastDate = lastInstance ? new Date(lastInstance.scheduled_date) : new Date();
      if (lastDate >= endDate) {
        return; // Already at or past end date
      }
    }

    setCreatingInstances(prev => new Set(prev).add(job.id));

    try {
      await jobService.createRecurringInstancesFromDate(
        job.id,
        currentCompany.id,
        job.jobDisplayId,
        job.recurringSchedule,
        5, // Create 5 instances
        lastInstance?.scheduled_date, // Start from after the last instance
        job.recurringEndDate
      );

      // Reload jobs to get the new instances
      await loadJobs();
    } catch (err) {
      console.error('Failed to create more instances:', err);
      alert(`Failed to create instances: ${err?.message}`);
    } finally {
      setCreatingInstances(prev => {
        const next = new Set(prev);
        next.delete(job.id);
        return next;
      });
    }
  };

  // Check if more instances can be created for a recurring job
  const canCreateMoreInstances = (job) => {
    if (!job || job.jobType === 'one_off') return false;
    if (!job.recurringSchedule?.days?.length) return false;

    // If no end date, can always create more
    if (!job.recurringEndDate) return true;

    // Check if last instance is before end date
    const instances = job?.job_instances || [];
    if (instances.length === 0) return true;

    const lastInstance = instances.reduce((latest, inst) => {
      const instDate = new Date(inst?.scheduled_date);
      return instDate > new Date(latest?.scheduled_date) ? inst : latest;
    }, instances[0]);

    const lastDate = new Date(lastInstance?.scheduled_date);
    const endDate = new Date(job.recurringEndDate);

    return lastDate < endDate;
  };

  // Handle sort column click
  const handleSort = (column) => {
    setSortConfig(prev => {
      if (prev.column === column) {
        // Toggle direction
        if (column === 'date' || column === 'time') {
          // For date/time, cycle through: desc -> asc -> desc
          return {
            ...prev,
            dateDirection: prev.dateDirection === 'desc' ? 'asc' : 'desc',
            timeDirection: column === 'time'
              ? (prev.timeDirection === 'desc' ? 'asc' : 'desc')
              : prev.timeDirection
          };
        }
        return {
          ...prev,
          dateDirection: prev.dateDirection === 'desc' ? 'asc' : 'desc'
        };
      }
      // New column
      return {
        column,
        dateDirection: 'desc',
        timeDirection: 'desc'
      };
    });
  };

  // Selection handlers
  const handleSelectAll = () => {
    if (selectedJobs.size === filteredJobs?.length) {
      setSelectedJobs(new Set());
    } else {
      setSelectedJobs(new Set(filteredJobs?.map(j => j?.id)));
    }
  };

  const handleSelectJob = (jobId) => {
    // Find the job to get its instances
    const job = filteredJobs?.find(j => j?.id === jobId);
    const instanceIds = job?.job_instances?.map(i => i?.id) || [];

    setSelectedJobs(prev => {
      const next = new Set(prev);
      const isCurrentlySelected = next.has(jobId);

      if (isCurrentlySelected) {
        next.delete(jobId);
      } else {
        next.add(jobId);
      }

      // Cascade to instances - select/deselect all instances of this job
      setSelectedInstances(prevInst => {
        const nextInst = new Set(prevInst);
        instanceIds.forEach(id => {
          if (isCurrentlySelected) {
            nextInst.delete(id);
          } else {
            nextInst.add(id);
          }
        });
        return nextInst;
      });

      return next;
    });
  };

  const handleSelectReceiverJobs = (jobs) => {
    const jobIds = jobs?.map(j => j?.id) || [];
    const allSelected = jobIds.every(id => selectedJobs.has(id));

    setSelectedJobs(prev => {
      const next = new Set(prev);
      if (allSelected) {
        jobIds.forEach(id => next.delete(id));
      } else {
        jobIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  // Instance selection handlers
  const handleSelectInstance = (instanceId) => {
    setSelectedInstances(prev => {
      const next = new Set(prev);
      if (next.has(instanceId)) {
        next.delete(instanceId);
      } else {
        next.add(instanceId);
      }
      return next;
    });
  };

  const handleSelectAllInstancesForJob = (instances) => {
    const instanceIds = instances?.map(i => i?.id) || [];
    const allSelected = instanceIds.every(id => selectedInstances.has(id));

    setSelectedInstances(prev => {
      const next = new Set(prev);
      if (allSelected) {
        instanceIds.forEach(id => next.delete(id));
      } else {
        instanceIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  // Bulk assign contractor to selected instances
  const handleBulkAssignInstances = async (contractorId) => {
    const instanceIds = Array.from(selectedInstances);
    if (instanceIds.length === 0) return;

    try {
      await Promise.all(
        instanceIds.map(id => jobService.assignContractor(id, contractorId))
      );
      setSelectedInstances(new Set());
      await loadJobs();
    } catch (err) {
      console.error('Failed to bulk assign:', err);
      alert(`Failed to assign contractor: ${err?.message}`);
    }
  };

  // Open modal for assignment (navigates to assignment tab with focus)
  const openForAssignment = (job) => {
    setSelectedJob(job);
    setModalInitialTab('assignment');
    setFocusContractorSearch(true);
    setShowCreateModal(true);
  };

  // Open modal for normal edit
  const openForEdit = (job) => {
    setSelectedJob(job);
    setModalInitialTab(null);
    setFocusContractorSearch(false);
    setShowCreateModal(true);
  };

  // Handle modal close - reset state
  const handleModalClose = () => {
    setShowCreateModal(false);
    setSelectedJob(null);
    setModalInitialTab(null);
    setFocusContractorSearch(false);
  };

  // Load jobs when company changes
  useEffect(() => {
    if (currentCompany?.id) {
      loadJobs();
    }
  }, [currentCompany?.id]);

  useEffect(() => {
    filterJobs();
  }, [jobs, searchTerm, statusFilter, showPendingOnly]);

  // Handle URL parameters for highlighting instances from notifications
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    const jobId = searchParams.get('jobId');

    if (highlightId && jobs.length > 0) {
      setHighlightedInstanceId(highlightId);

      // Find the job containing this instance
      const targetJob = jobs.find(job =>
        job?.job_instances?.some(inst => inst?.id === highlightId)
      );

      if (targetJob) {
        // Get client and job site info to expand the hierarchy
        const clientId = targetJob?.clientId || targetJob?.client?.id || 'unassigned';
        const jobSiteId = targetJob?.jobSiteId || targetJob?.job_site?.id || targetJob?.jobSite?.id || 'unassigned';

        // Expand the client
        setExpandedClients(prev => new Set([...prev, clientId]));

        // Expand the job site
        setExpandedJobSites(prev => new Set([...prev, `${clientId}-${jobSiteId}`]));

        // Expand the job to show instances
        setExpandedJobs(prev => new Set([...prev, targetJob.id]));

        // Scroll to the instance after a short delay to allow expansion
        setTimeout(() => {
          const element = document.getElementById(`instance-row-${highlightId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);

        // Clear the URL params after handling
        setTimeout(() => {
          setSearchParams({});
        }, 500);

        // Clear highlight after a few seconds
        setTimeout(() => {
          setHighlightedInstanceId(null);
        }, 5000);
      }
    }
  }, [searchParams, jobs]);

  const loadJobs = async (silent = false) => {
    if (!currentCompany?.id) {
      setLoading(false);
      return;
    }

    try {
      // Only show loading state if not silent
      if (!silent) {
        setLoading(true);
      }

      // Update pending job flags before loading jobs
      // This ensures is_pending and is_pending_report flags are current
      await jobService?.updatePendingJobFlags(currentCompany.id);

      const data = await jobService?.getCompanyJobs(currentCompany.id);
      setJobs(data);
    } catch (err) {
      setError(err?.message);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  // Update a specific instance's contractor in local state (no reload needed)
  const updateInstanceContractorLocally = (instanceId, contractor) => {
    setJobs(prevJobs => {
      return prevJobs?.map(job => {
        const updatedInstances = job?.job_instances?.map(instance => {
          if (instance?.id === instanceId) {
            return {
              ...instance,
              assigned_contractor: contractor?.userId || contractor?.id,
              contractor: {
                id: contractor?.userId || contractor?.id,
                full_name: contractor?.fullName,
                email: contractor?.email
              },
              status: 'assigned'
            };
          }
          return instance;
        });
        return { ...job, job_instances: updatedInstances };
      });
    });
  };

  // Update instance schedule locally (no reload needed)
  const updateInstanceScheduleLocally = (instanceId, updates) => {
    setJobs(prevJobs => {
      return prevJobs?.map(job => {
        const updatedInstances = job?.job_instances?.map(instance => {
          if (instance?.id === instanceId) {
            return {
              ...instance,
              scheduled_date: updates.scheduled_date,
              scheduled_time: updates.scheduled_time
            };
          }
          return instance;
        });
        return { ...job, job_instances: updatedInstances };
      });
    });
  };

  // Delete instance handler
  const handleDeleteInstance = async (instanceId, jobId) => {
    if (!window.confirm('Are you sure you want to delete this instance?')) return;

    try {
      await jobService.deleteJobInstance(instanceId);
      // Remove locally
      setJobs(prevJobs => {
        return prevJobs?.map(job => {
          if (job?.id === jobId) {
            return {
              ...job,
              job_instances: job?.job_instances?.filter(i => i?.id !== instanceId)
            };
          }
          return job;
        });
      });
      // Also remove from selection if selected
      setSelectedInstances(prev => {
        const next = new Set(prev);
        next.delete(instanceId);
        return next;
      });
    } catch (err) {
      console.error('Failed to delete instance:', err);
      alert(`Failed to delete instance: ${err?.message || 'Unknown error'}`);
    }
  };

  const filterJobs = () => {
    let filtered = jobs;

    // Filter out jobs where ALL instances are in reports mode (submitted/approved/paid)
    // These jobs should only appear in Reports & Earnings
    filtered = filtered?.filter(job => {
      const instances = job?.job_instances || [];
      // If no instances, keep the job (it's still being set up)
      if (instances.length === 0) return true;
      // Check if at least one instance is NOT in reports mode
      const hasManageableInstance = instances.some(inst => !isInstanceInReportsMode(inst));
      return hasManageableInstance;
    });

    if (statusFilter !== 'all') {
      filtered = filtered?.filter(job => job?.status === statusFilter);
    }

    if (searchTerm) {
      filtered = filtered?.filter(job =>
        job?.jobName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        job?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    // Filter for pending jobs only - keep hierarchy but only show pending instances
    if (showPendingOnly) {
      filtered = filtered
        ?.map(job => {
          const instances = job?.job_instances || [];
          // Filter to only keep instances with pending flags
          const pendingInstances = instances.filter(inst => inst?.is_pending || inst?.is_pending_report);
          // Return job with only pending instances (preserves hierarchy)
          return {
            ...job,
            job_instances: pendingInstances
          };
        })
        // Remove jobs that have no pending instances after filtering
        ?.filter(job => job?.job_instances?.length > 0);
    }

    setFilteredJobs(filtered);
  };

  // Count total pending jobs for the filter button badge
  const getPendingJobsCount = () => {
    let count = 0;
    jobs?.forEach(job => {
      const instances = job?.job_instances || [];
      instances.forEach(inst => {
        if (inst?.is_pending || inst?.is_pending_report) {
          count++;
        }
      });
    });
    return count;
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;

    try {
      await jobService?.deleteJob(jobId);
      loadJobs();
    } catch (err) {
      alert(`Failed to delete job: ${err?.message}`);
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      draft: 'bg-gray-500',
      active: 'bg-blue-500',
      paused: 'bg-yellow-500',
      completed: 'bg-green-500',
      cancelled: 'bg-red-500',
      // Instance-specific statuses
      created: 'bg-gray-500',
      unassigned: 'bg-gray-500',
      assigned: 'bg-blue-500',
      in_progress: 'bg-yellow-500',
      submitted: 'bg-purple-500',
      approved: 'bg-green-500',
      paid: 'bg-green-600',
      needs_revision: 'bg-orange-500',
      disputed: 'bg-red-400',
      rejected: 'bg-red-500',
      under_review: 'bg-indigo-500'
    };
    return classes?.[status] || 'bg-gray-500';
  };

  // Check if an instance status indicates it needs assignment
  const isUnassignedStatus = (status) => {
    return !status || status === 'created' || status === 'unassigned';
  };

  const getPriorityBadgeClass = (priority) => {
    const classes = {
      low: 'bg-gray-400',
      medium: 'bg-yellow-500',
      high: 'bg-red-500'
    };
    return classes?.[priority] || 'bg-gray-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-text-primary text-xl">Loading jobs...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header />

      {/* Sidebar */}
      <Sidebar />

      {/* Main Content - with margin for fixed header and sidebar */}
      <main className="ml-0 lg:ml-60 mt-16 p-8 bg-background min-h-screen text-text-primary">
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-400">
            {error}
          </div>
        )}
        {/* Toolbar */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          {/* Search and Pending Filter */}
          <div className="flex items-center gap-3 flex-1">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-600" />
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 bg-surface border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-primary-orange"
              />
            </div>

            {/* Pending Jobs Filter Button - Critical/Alarming Style */}
            <button
              onClick={() => setShowPendingOnly(!showPendingOnly)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all border-2',
                showPendingOnly
                  ? 'bg-red-500 text-white border-red-500 shadow-lg shadow-red-500/30'
                  : getPendingJobsCount() > 0
                    ? 'bg-red-500/10 text-red-400 border-red-500 hover:bg-red-500/20 animate-pulse'
                    : 'bg-surface text-text-secondary border-border hover:border-text-tertiary'
              )}
            >
              <AlertTriangle className={cn(
                'w-5 h-5',
                showPendingOnly ? 'text-white' : getPendingJobsCount() > 0 ? 'text-red-400' : 'text-text-tertiary'
              )} />
              <span className="whitespace-nowrap">
                {showPendingOnly ? 'Showing Pending Jobs' : 'Total Pending Jobs'}
              </span>
              {getPendingJobsCount() > 0 && (
                <span className={cn(
                  'px-2 py-0.5 rounded-full text-xs font-bold',
                  showPendingOnly
                    ? 'bg-white text-red-500'
                    : 'bg-red-500 text-white'
                )}>
                  {getPendingJobsCount()}
                </span>
              )}
            </button>
          </div>

          {/* Filters & Create Button */}
          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e?.target?.value)}
              className="px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary-orange"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="completed">Completed</option>
              <option value="paused">Paused</option>
            </select>

            <button
              onClick={() => {
                setSelectedJob(null);
                setShowCreateModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-primary-orange/90 font-medium transition-colors"
            >
              <Plus className="w-5 h-5" />
              Create Job
            </button>
          </div>
        </div>

        {/* Bulk Instance Selection Actions */}
        {selectedInstances.size > 0 && (
          <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-surface rounded-lg border border-border">
            <span className="text-sm text-text-primary">
              {selectedInstances.size} instance{selectedInstances.size !== 1 ? 's' : ''} selected
            </span>
            <button
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setAssigningInstance({
                  instance: null, // null indicates bulk mode
                  job: null,
                  position: { top: rect.bottom + 4, left: rect.left },
                  isBulk: true
                });
              }}
              className="px-3 py-1.5 bg-primary-orange text-white text-sm rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-1"
            >
              <User className="w-4 h-4" />
              Assign Contractor
            </button>
            <button
              onClick={() => setSelectedInstances(new Set())}
              className="text-text-secondary hover:text-text-primary transition-colors"
              title="Clear selection"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Jobs Table */}
        <div className="bg-surface rounded-lg overflow-hidden border border-border">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background">
                <tr>
                  {/* Checkbox column */}
                  <th className="px-4 py-4 text-left w-12">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filteredJobs?.length > 0 && selectedJobs.size === filteredJobs?.length}
                        onChange={handleSelectAll}
                        className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent focus:ring-offset-0"
                      />
                      <span className="text-xs font-semibold text-text-secondary">All</span>
                    </label>
                  </th>
                  {/* Client/Site column - sortable */}
                  <th className="px-4 py-4 text-left">
                    <button
                      onClick={() => handleSort('client')}
                      className="flex items-center gap-1 text-sm font-semibold text-text-secondary hover:text-text-primary transition-colors"
                    >
                      Client / Site
                      {sortConfig.column === 'client' && sortConfig.dateDirection === 'asc'
                        ? <ArrowUp className="w-3 h-3 text-accent" />
                        : <ArrowDown className={cn('w-3 h-3', sortConfig.column === 'client' ? 'text-accent' : 'text-gray-600')} />
                      }
                    </button>
                  </th>
                  {/* Job Name - not sortable */}
                  <th className="px-4 py-4 text-left text-sm font-semibold text-text-secondary">Job Name</th>
                  {/* Priority - sortable */}
                  <th className="px-4 py-4 text-left">
                    <button
                      onClick={() => handleSort('priority')}
                      className="flex items-center gap-1 text-sm font-semibold text-text-secondary hover:text-text-primary transition-colors"
                    >
                      Priority
                      {sortConfig.column === 'priority' && sortConfig.dateDirection === 'asc'
                        ? <ArrowUp className="w-3 h-3 text-accent" />
                        : <ArrowDown className={cn('w-3 h-3', sortConfig.column === 'priority' ? 'text-accent' : 'text-gray-600')} />
                      }
                    </button>
                  </th>
                  {/* Status - sortable */}
                  <th className="px-4 py-4 text-left">
                    <button
                      onClick={() => handleSort('status')}
                      className="flex items-center gap-1 text-sm font-semibold text-text-secondary hover:text-text-primary transition-colors"
                    >
                      Status
                      {sortConfig.column === 'status' && sortConfig.dateDirection === 'asc'
                        ? <ArrowUp className="w-3 h-3 text-accent" />
                        : <ArrowDown className={cn('w-3 h-3', sortConfig.column === 'status' ? 'text-accent' : 'text-gray-600')} />
                      }
                    </button>
                  </th>
                  {/* Payment - sortable */}
                  <th className="px-4 py-4 text-left">
                    <button
                      onClick={() => handleSort('payment')}
                      className="flex items-center gap-1 text-sm font-semibold text-text-secondary hover:text-text-primary transition-colors"
                    >
                      Payment
                      {sortConfig.column === 'payment' && sortConfig.dateDirection === 'asc'
                        ? <ArrowUp className="w-3 h-3 text-accent" />
                        : <ArrowDown className={cn('w-3 h-3', sortConfig.column === 'payment' ? 'text-accent' : 'text-gray-600')} />
                      }
                    </button>
                  </th>
                  {/* Schedule - sortable (combines Date & Time) */}
                  <th className="px-4 py-4 text-left">
                    <button
                      onClick={() => handleSort('date')}
                      className="flex items-center gap-1 text-sm font-semibold text-text-secondary hover:text-text-primary transition-colors"
                    >
                      Schedule
                      {sortConfig.column === 'date' && sortConfig.dateDirection === 'asc'
                        ? <ArrowUp className="w-3 h-3 text-accent" />
                        : <ArrowDown className={cn('w-3 h-3', sortConfig.column === 'date' ? 'text-accent' : 'text-gray-600')} />
                      }
                    </button>
                  </th>
                  {/* Created - sortable */}
                  <th className="px-4 py-4 text-left">
                    <button
                      onClick={() => handleSort('created')}
                      className="flex items-center gap-1 text-sm font-semibold text-text-secondary hover:text-text-primary transition-colors"
                    >
                      Created
                      {sortConfig.column === 'created' && sortConfig.dateDirection === 'asc'
                        ? <ArrowUp className="w-3 h-3 text-accent" />
                        : <ArrowDown className={cn('w-3 h-3', sortConfig.column === 'created' ? 'text-accent' : 'text-gray-600')} />
                      }
                    </button>
                  </th>
                  {/* Actions - not sortable */}
                  <th className="px-4 py-4 text-right text-sm font-semibold text-text-secondary">Actions</th>
                </tr>
                {/* Gradient separator after header */}
                <tr>
                  <td colSpan="9" className="p-0">
                    <div className="h-1 bg-gradient-to-r from-[#FF7A00] via-[#00C47D] to-[#FF7A00]" />
                  </td>
                </tr>
              </thead>
              <tbody>
                {filteredJobs?.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-8 text-center text-text-secondary">
                      No jobs found. Create your first job to get started.
                    </td>
                  </tr>
                ) : (
                  (() => {
                    // Sort jobs first
                    const sortedJobs = sortJobs(filteredJobs, sortConfig);
                    // Group by Client → Job Site hierarchy
                    const clientGroups = groupJobsByClientAndJobSite(sortedJobs);

                    return Array.from(clientGroups.entries()).map(([clientId, { client, jobSites }]) => {
                      const isClientExpanded = expandedClients.has(clientId);
                      // Count total jobs across all job sites for this client
                      let totalClientJobs = 0;
                      jobSites.forEach(({ jobs }) => { totalClientJobs += jobs.length; });

                      // Get all jobs for this client for selection
                      const allClientJobs = [];
                      jobSites.forEach(({ jobs }) => { allClientJobs.push(...jobs); });
                      const allClientJobsSelected = allClientJobs.every(j => selectedJobs.has(j?.id));
                      const someClientJobsSelected = allClientJobs.some(j => selectedJobs.has(j?.id));

                      return (
                        <React.Fragment key={clientId}>
                          {/* Client Row - Top Level */}
                          <tr
                            className={cn(
                              'bg-surface-dark cursor-pointer hover:brightness-110 transition-all',
                              client.isUnassigned ? 'border-l-4 border-l-gray-500' : 'border-l-4 border-l-primary-orange'
                            )}
                            onClick={() => toggleClient(clientId)}
                          >
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={allClientJobsSelected}
                                ref={el => {
                                  if (el) el.indeterminate = someClientJobsSelected && !allClientJobsSelected;
                                }}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleSelectReceiverJobs(allClientJobs);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent focus:ring-offset-0"
                              />
                            </td>
                            <td colSpan="8" className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {isClientExpanded ? (
                                  <ChevronDown className="w-5 h-5 text-accent" />
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-text-secondary" />
                                )}
                                <Building className="w-5 h-5 text-primary-orange" />
                                <span className="font-semibold text-text-primary">
                                  {client.name}
                                </span>
                                {client.tag && (
                                  <span className="px-1.5 py-0.5 bg-accent/10 text-accent text-xs rounded font-medium">
                                    {client.tag}
                                  </span>
                                )}
                                <span className="text-xs text-text-secondary bg-border px-2 py-0.5 rounded ml-2">
                                  {totalClientJobs} job{totalClientJobs !== 1 ? 's' : ''}
                                </span>
                              </div>
                            </td>
                          </tr>

                          {/* Job Sites within this Client */}
                          {isClientExpanded && Array.from(jobSites.entries()).map(([jobSiteId, { jobSite, jobs: siteJobs }], siteIdx) => {
                            const isOdd = siteIdx % 2 === 0;
                            const colors = isOdd ? RECEIVER_COLORS.odd : RECEIVER_COLORS.even;
                            const isJobSiteExpanded = expandedJobSites.has(`${clientId}-${jobSiteId}`);
                            const allJobsSelected = siteJobs.every(j => selectedJobs.has(j?.id));
                            const someJobsSelected = siteJobs.some(j => selectedJobs.has(j?.id));

                            return (
                              <React.Fragment key={`${clientId}-${jobSiteId}`}>
                                {/* Job Site Row */}
                                <tr
                                  className={cn(
                                    colors.receiver,
                                    'cursor-pointer hover:brightness-110 transition-all'
                                  )}
                                  onClick={() => toggleJobSite(`${clientId}-${jobSiteId}`)}
                                >
                                  <td className="px-4 py-3 pl-8">
                                    <input
                                      type="checkbox"
                                      checked={allJobsSelected}
                                      ref={el => {
                                        if (el) el.indeterminate = someJobsSelected && !allJobsSelected;
                                      }}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        handleSelectReceiverJobs(siteJobs);
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent focus:ring-offset-0"
                                    />
                                  </td>
                                  <td colSpan="8" className="px-4 py-3">
                                    <div className="flex items-center gap-2 pl-4">
                                      {isJobSiteExpanded ? (
                                        <ChevronDown className="w-4 h-4 text-accent" />
                                      ) : (
                                        <ChevronRight className="w-4 h-4 text-text-secondary" />
                                      )}
                                      <Building2 className="w-4 h-4 text-accent" />
                                      <span className="font-medium text-text-primary truncate max-w-[200px]">
                                        {jobSite.name}
                                      </span>
                                      {jobSite.address && (
                                        <span className="text-xs text-text-tertiary flex items-center gap-1 truncate max-w-[250px]">
                                          <MapPin className="w-3 h-3" />
                                          {jobSite.address}
                                        </span>
                                      )}
                                      <span className="text-xs text-text-secondary bg-border px-2 py-0.5 rounded">
                                        {siteJobs.length} job{siteJobs.length !== 1 ? 's' : ''}
                                      </span>
                                    </div>
                                  </td>
                                </tr>

                                {/* Job Rows (when job site is expanded) */}
                                {isJobSiteExpanded && siteJobs.map((job, jobIndex) => {
                            const displayStatus = computeJobStatus(job);
                            // Filter out instances that are in Reports mode (submitted/approved/paid/disputed)
                            const allInstances = job?.job_instances || [];
                            const instances = filterInstancesForManagement(allInstances);
                            // Only show instance dropdown for recurring jobs or jobs with multiple instances
                            // One-off jobs with single date/time don't need a dropdown
                            const isOneOffJob = job?.jobType === 'one_off';
                            const showInstanceDropdown = instances.length > 1 || (instances.length > 0 && !isOneOffJob);
                            const isJobExpanded = expandedJobs.has(job?.id);
                            const currentPage = instancePages[job?.id] || 1;
                            // Sort instances before pagination
                            const sortedInstances = sortInstances(instances, sortConfig);
                            const visibleInstances = sortedInstances.slice(0, currentPage * 5);
                            const hasMoreInstances = sortedInstances.length > visibleInstances.length;
                            const isLastJob = jobIndex === siteJobs.length - 1;

                            // Calculate indeterminate state for job checkbox
                            const instanceIds = instances.map(i => i?.id);
                            const selectedInstanceCount = instanceIds.filter(id => selectedInstances.has(id)).length;
                            const allInstancesSelected = instanceIds.length > 0 && selectedInstanceCount === instanceIds.length;
                            const someInstancesSelected = selectedInstanceCount > 0 && selectedInstanceCount < instanceIds.length;

                            return (
                              <React.Fragment key={job?.id}>
                                {/* Job Row */}
                                <tr
                                  className={cn(
                                    colors.job,
                                    !isLastJob && 'border-b border-dashed border-accent/40',
                                    showInstanceDropdown && 'cursor-pointer hover:brightness-110',
                                    'transition-all'
                                  )}
                                  onClick={() => showInstanceDropdown && toggleJob(job?.id)}
                                >
                                  {/* Checkbox with indeterminate state */}
                                  <td className="px-4 py-3">
                                    <input
                                      type="checkbox"
                                      checked={selectedJobs.has(job?.id) || allInstancesSelected}
                                      ref={(el) => { if (el) el.indeterminate = someInstancesSelected; }}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        handleSelectJob(job?.id);
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent focus:ring-offset-0"
                                    />
                                  </td>
                                  {/* Instances (indent for hierarchy) */}
                                  <td className="px-4 py-3 pl-10">
                                    {showInstanceDropdown && (
                                      <span className="inline-flex items-center">
                                        {isJobExpanded ? (
                                          <ChevronDown className="w-3 h-3 text-accent mr-1" />
                                        ) : (
                                          <ChevronRight className="w-3 h-3 text-text-secondary mr-1" />
                                        )}
                                      </span>
                                    )}
                                    {showInstanceDropdown ? (
                                      <span className="text-xs text-text-secondary">
                                        {instances.length} instance{instances.length !== 1 ? 's' : ''}
                                      </span>
                                    ) : isOneOffJob ? (
                                      <span className="text-xs text-text-secondary">One-off</span>
                                    ) : null}
                                  </td>
                                  {/* Job Name */}
                                  <td className="px-4 py-3">
                                    <div>
                                      <div className="font-medium text-text-primary">{job?.jobName}</div>
                                      {job?.description && (
                                        <div className="text-sm text-text-secondary mt-1 line-clamp-1">
                                          {job?.description}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  {/* Priority */}
                                  <td className="px-4 py-3">
                                    <span className={cn(
                                      'inline-flex items-center justify-center min-w-[70px] px-3 py-1.5 rounded text-xs font-medium text-white',
                                      getPriorityBadgeClass(job?.priority)
                                    )}>
                                      {job?.priority?.toUpperCase()}
                                    </span>
                                  </td>
                                  {/* Status */}
                                  <td className="px-4 py-3">
                                    <span className={cn(
                                      'inline-flex items-center justify-center min-w-[80px] px-3 py-1.5 rounded text-xs font-medium text-white capitalize',
                                      getStatusBadgeClass(displayStatus)
                                    )}>
                                      {displayStatus}
                                    </span>
                                  </td>
                                  {/* Payment */}
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-1 text-sm">
                                      <DollarSign className="w-4 h-4 text-accent-green" />
                                      <span className="text-text-primary">{job?.paymentAmount || '—'}</span>
                                      <span className="text-text-secondary text-xs">
                                        {job?.paymentType === 'hourly' ? '/hr' : 'fixed'}
                                      </span>
                                    </div>
                                  </td>
                                  {/* Schedule (combined Date & Time) */}
                                  <td className="px-4 py-3 text-sm text-text-secondary">
                                    {job?.jobType === 'one_off'
                                      ? formatOneTimeSchedule(job?.scheduledDate, job?.scheduledTime, preferences.date_format, preferences.time_format)
                                      : job?.recurringSchedule
                                        ? <RecurringDaysDisplay schedule={job?.recurringSchedule} timeFormat={preferences.time_format} />
                                        : '—'}
                                  </td>
                                  {/* Created */}
                                  <td className="px-4 py-3 text-sm text-text-secondary">
                                    {formatCreatedDate(job?.createdAt, preferences.date_format)}
                                  </td>
                                  {/* Actions */}
                                  <td className="px-4 py-3">
                                    <div className="flex items-center justify-end gap-1">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setViewingJob(job);
                                        }}
                                        className="p-1.5 hover:bg-white/10 rounded transition-colors"
                                        title="View job details"
                                      >
                                        <Eye className="w-4 h-4 text-text-primary" />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openForEdit(job);
                                        }}
                                        className="p-1.5 hover:bg-white/10 rounded transition-colors"
                                        title="Edit job"
                                      >
                                        <Edit2 className="w-4 h-4 text-text-primary" />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteJob(job?.id);
                                        }}
                                        className="p-1.5 hover:bg-red-500/10 text-red-400 rounded transition-colors"
                                        title="Delete job"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>

                                {/* Instance Rows (when job is expanded and has instances) */}
                                {isJobExpanded && showInstanceDropdown && (
                                  <>
                                    {visibleInstances.map((instance, instIndex) => {
                                      const isLastInstance = instIndex === visibleInstances.length - 1 && !hasMoreInstances;
                                      const isPastCompleted = isInstancePastCompleted(instance);
                                      const isHighlighted = highlightedInstanceId === instance?.id;
                                      return (
                                        <tr
                                          key={instance?.id}
                                          id={`instance-row-${instance?.id}`}
                                          className={cn(
                                            colors.instance,
                                            !isLastInstance && 'border-b border-dotted border-gray-600/50',
                                            isPastCompleted && 'opacity-60',
                                            isHighlighted && 'ring-2 ring-orange-500 ring-offset-2 ring-offset-background bg-orange-500/10 animate-pulse'
                                          )}
                                        >
                                          {/* Instance checkbox */}
                                          <td className="px-4 py-2">
                                            <input
                                              type="checkbox"
                                              checked={selectedInstances.has(instance?.id)}
                                              onChange={(e) => {
                                                e.stopPropagation();
                                                handleSelectInstance(instance?.id);
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                              className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent focus:ring-offset-0"
                                            />
                                          </td>
                                          {/* Instance number - always use stored instance_number */}
                                          <td className="px-4 py-2 pl-14">
                                            <span className="text-xs text-text-tertiary">#{instance?.instance_number || instIndex + 1}</span>
                                          </td>
                                          {/* Instance contractor name */}
                                          <td className="px-4 py-2">
                                            <span className={cn(
                                              "text-sm",
                                              instance?.contractor?.full_name || instance?.contractor?.email
                                                ? "text-text-secondary"
                                                : "text-text-tertiary italic"
                                            )}>
                                              {instance?.contractor?.full_name || instance?.contractor?.email || 'Unassigned'}
                                            </span>
                                          </td>
                                          {/* Empty priority */}
                                          <td className="px-4 py-2"></td>
                                          {/* Instance status badge */}
                                          <td className="px-4 py-2">
                                            <div className="flex flex-col gap-1">
                                              <span className={cn(
                                                'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                                                getInstanceStatusColor(instance?.status)
                                              )}>
                                                {formatStatusText(instance?.status)}
                                              </span>
                                              {/* Pending badge - shown below status */}
                                              {instance?.is_pending && (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/50">
                                                  <AlertTriangle className="w-3 h-3" />
                                                  Pending
                                                </span>
                                              )}
                                              {/* Pending Report badge - shown below status */}
                                              {instance?.is_pending_report && (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-orange-500/20 text-orange-400 border border-orange-500/50">
                                                  <AlertTriangle className="w-3 h-3" />
                                                  Pending Report
                                                </span>
                                              )}
                                            </div>
                                          </td>
                                          {/* Empty payment */}
                                          <td className="px-4 py-2"></td>
                                          {/* Instance schedule (combined date & time) */}
                                          <td className="px-4 py-2 text-sm text-text-secondary">
                                            {formatInstanceSchedule(instance?.scheduled_date, instance?.scheduled_time, preferences.date_format, preferences.time_format)}
                                          </td>
                                          {/* Empty created */}
                                          <td className="px-4 py-2"></td>
                                          {/* Instance actions */}
                                          <td className="px-4 py-2">
                                            <div className="flex items-center justify-end gap-1">
                                              {/* Reassign Contractor */}
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  const rect = e.currentTarget.getBoundingClientRect();
                                                  setAssigningInstance({
                                                    instance,
                                                    job,
                                                    position: { top: rect.bottom + 4, left: rect.left - 200 }
                                                  });
                                                }}
                                                className="p-1 hover:bg-white/10 rounded transition-colors"
                                                title={instance?.contractor ? "Reassign contractor" : "Assign contractor"}
                                              >
                                                <ReassignIcon className="w-4 h-4" />
                                              </button>
                                              {/* View */}
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setViewingInstance({ job, instance });
                                                }}
                                                className="p-1 hover:bg-white/10 rounded transition-colors"
                                                title="View instance report"
                                              >
                                                <Eye className="w-3 h-3 text-text-secondary" />
                                              </button>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  const rect = e.currentTarget.getBoundingClientRect();
                                                  setEditingInstanceSchedule({
                                                    instance,
                                                    job,
                                                    position: { top: rect.bottom + 4, left: rect.left - 200 }
                                                  });
                                                }}
                                                className="p-1 hover:bg-white/10 rounded transition-colors"
                                                title="Edit schedule"
                                              >
                                                <Edit2 className="w-3 h-3 text-text-secondary" />
                                              </button>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeleteInstance(instance?.id, job?.id);
                                                }}
                                                className="p-1 hover:bg-red-500/10 text-red-400 rounded transition-colors"
                                                title="Delete instance"
                                              >
                                                <Trash2 className="w-3 h-3" />
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}

                                    {/* Show more button */}
                                    {hasMoreInstances && (
                                      <tr className={cn(colors.instance)}>
                                        <td colSpan="9" className="px-4 py-2">
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              loadMoreInstances(job?.id);
                                            }}
                                            className="text-xs text-accent hover:text-accent-600 transition-colors ml-14"
                                          >
                                            Show 5 more ({instances.length - visibleInstances.length} remaining)
                                          </button>
                                        </td>
                                      </tr>
                                    )}

                                    {/* Create more instances button for recurring jobs */}
                                    {!isOneOffJob && canCreateMoreInstances(job) && (
                                      <tr className={cn(colors.instance)}>
                                        <td colSpan="9" className="px-4 py-2">
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              createMoreInstances(job);
                                            }}
                                            disabled={creatingInstances.has(job?.id)}
                                            className={cn(
                                              "text-xs ml-14 transition-colors inline-flex items-center gap-1",
                                              creatingInstances.has(job?.id)
                                                ? "text-text-tertiary cursor-not-allowed"
                                                : "text-primary-orange hover:text-orange-400"
                                            )}
                                          >
                                            {creatingInstances.has(job?.id) ? (
                                              <>
                                                <Loader2 className="w-3 h-3 animate-spin" />
                                                Creating instances...
                                              </>
                                            ) : (
                                              <>
                                                <Plus className="w-3 h-3" />
                                                Create 5 more instances
                                              </>
                                            )}
                                          </button>
                                        </td>
                                      </tr>
                                    )}

                                    {/* Message when end date is reached */}
                                    {!isOneOffJob && job?.recurringEndDate && !canCreateMoreInstances(job) && (
                                      <tr className={cn(colors.instance)}>
                                        <td colSpan="9" className="px-4 py-2">
                                          <span className="text-xs text-text-tertiary ml-14">
                                            All instances created up to end date
                                          </span>
                                        </td>
                                      </tr>
                                    )}

                                    {/* Job end date notice */}
                                    {job?.recurringEndDate && (
                                      <tr className={cn(colors.instance)}>
                                        <td colSpan="9" className="px-4 py-2">
                                          <span className="text-xs text-yellow-500 ml-14">
                                            Job ends on {formatDate(job?.recurringEndDate, preferences.date_format)}
                                          </span>
                                        </td>
                                      </tr>
                                    )}
                                  </>
                                )}
                              </React.Fragment>
                            );
                          })}
                              </React.Fragment>
                            );
                          })}

                          {/* Agency Separator - Double line with diamond */}
                          <tr>
                            <td colSpan="9" className="p-0">
                              <div className="relative">
                                {/* Top line */}
                                <div className="h-[2px] bg-accent" />
                                {/* Bottom line */}
                                <div className="h-[1px] bg-accent/50 mt-[2px]" />
                              </div>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    });
                  })()
                )}
              </tbody>
            </table>
          </div>
        </div>
        {/* Create Job Modal */}
        <CreateJobModal
          isOpen={showCreateModal}
          onClose={handleModalClose}
          onJobCreated={(newJob) => {
            loadJobs();
          }}
          jobToEdit={selectedJob}
          initialTab={modalInitialTab}
          focusContractorSearch={focusContractorSearch}
        />

        {/* Instance Contractor Assignment Popover */}
        {assigningInstance && (
          <InstanceContractorPopover
            instanceId={assigningInstance?.instance?.id}
            instanceDisplayId={assigningInstance?.instance?.instance_display_id || `#${assigningInstance?.instance?.instance_number || ''}`}
            currentContractor={assigningInstance?.instance?.contractor}
            companyId={currentCompany?.id}
            onAssigned={(contractor) => {
              if (assigningInstance?.isBulk) {
                // Bulk assignment - update all selected instances locally
                const instanceIds = Array.from(selectedInstances);
                instanceIds.forEach(id => updateInstanceContractorLocally(id, contractor));
                setSelectedInstances(new Set());
              } else if (assigningInstance?.instance?.id) {
                // Single instance - update locally
                updateInstanceContractorLocally(assigningInstance.instance.id, contractor);
              }
              // Also do a silent background refresh to ensure consistency
              loadJobs(true);
            }}
            onClose={() => setAssigningInstance(null)}
            position={assigningInstance?.position}
            isBulk={assigningInstance?.isBulk || false}
            selectedInstanceIds={assigningInstance?.isBulk ? Array.from(selectedInstances) : []}
          />
        )}

        {/* Instance Schedule Edit Popover */}
        {editingInstanceSchedule && (
          <InstanceSchedulePopover
            instanceId={editingInstanceSchedule?.instance?.id}
            instanceDisplayId={editingInstanceSchedule?.instance?.instance_display_id || `#${editingInstanceSchedule?.instance?.instance_number || ''}`}
            currentDate={editingInstanceSchedule?.instance?.scheduled_date || ''}
            currentTime={editingInstanceSchedule?.instance?.scheduled_time || ''}
            onUpdated={(updates) => {
              // Update locally for immediate feedback
              updateInstanceScheduleLocally(updates?.id, updates);
              // Silent background refresh
              loadJobs(true);
            }}
            onClose={() => setEditingInstanceSchedule(null)}
            position={editingInstanceSchedule?.position}
          />
        )}

        {/* Job Invoice Modal (for job-level view) */}
        {viewingJob && (
          <JobInvoice
            job={viewingJob}
            instance={null}
            company={currentCompany}
            onClose={() => setViewingJob(null)}
          />
        )}

        {/* Instance Invoice Modal (for instance-level view) */}
        {viewingInstance && (
          <JobInvoice
            job={viewingInstance.job}
            instance={viewingInstance.instance}
            company={currentCompany}
            onClose={() => setViewingInstance(null)}
          />
        )}
      </main>
    </div>
  );
}